import React, { useState, useRef, useEffect } from 'react'
import { BiChevronDown } from 'react-icons/bi'
import { AiOutlineSearch } from 'react-icons/ai'
import { MdAddBox } from 'react-icons/md'
import { useCurrent } from '../../hooks';

const MwSelector = ({ label, _data, dataType, onClickNew, setSelectedItem, selectedItem, initalValue = 'Choose', withLabel = true, withAddNew = true, className, styleList, invalid, invalidMsg, showAvatarFallback = false, disabled = false }) => {
    const [inputValue, setInputValue] = useState('');
    const { currentLangId } = useCurrent();
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef(null);
    const currentDot = selectedItem && selectedItem !== 'sub' ? (selectedItem.dot || selectedItem.color || '') : '';
    const currentAvatar = selectedItem && selectedItem !== 'sub' ? (selectedItem.avatar || selectedItem.userPic || selectedItem.image || '') : '';
    const currentSubLabel = selectedItem && selectedItem !== 'sub' ? (selectedItem.subLabel || selectedItem.jobTitle || '') : '';
    const [avatarPreviewByUrl, setAvatarPreviewByUrl] = useState({});
    const [avatarFailedByUrl, setAvatarFailedByUrl] = useState({});
    const objectUrlRef = useRef({});

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        return () => {
            const urls = Object.values(objectUrlRef.current || {});
            urls.forEach((url) => {
                try { URL.revokeObjectURL(url); } catch { }
            });
            objectUrlRef.current = {};
        };
    }, []);

    const normalizeUploadsBaseUrl = (value) => String(value || '').replace(/\/+$/, '');
    const uploadsBaseUrl = normalizeUploadsBaseUrl(process.env.REACT_APP_UPLOADS_API_BASE_URL);
    const normalizeAvatarUrl = (raw) => {
        const value = String(raw || '').trim();
        if (!value) return '';
        if (/^https?:\/\//i.test(value) || value.startsWith('blob:') || value.startsWith('data:')) return value;
        const cleaned = value.replace(/^\/+/, '');
        return uploadsBaseUrl ? `${uploadsBaseUrl}/${cleaned}` : cleaned;
    };
    const getAvatarUrl = (item) => normalizeAvatarUrl(item?.avatar || item?.userPic || item?.image);

    // ✅ جلب معاينة الصورة للعنصر المختار دائماً (حتى لو كانت القائمة مغلقة)
    useEffect(() => {
        if (!currentAvatar) return;
        const url = normalizeAvatarUrl(currentAvatar);
        if (!url || url.startsWith('blob:') || url.startsWith('data:') || !/^https?:\/\//i.test(url)) return;
        if (avatarPreviewByUrl[url] || objectUrlRef.current[url]) return;

        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const controller = new AbortController();

        (async () => {
            try {
                const res = await fetch(url, { headers, signal: controller.signal });
                if (!res.ok) return;
                const blob = await res.blob();
                const objUrl = URL.createObjectURL(blob);
                objectUrlRef.current[url] = objUrl;
                setAvatarPreviewByUrl((prev) => ({ ...prev, [url]: objUrl }));
            } catch { }
        })();

        return () => { try { controller.abort(); } catch { } };
    }, [currentAvatar]);

    useEffect(() => {
        if (!open) return;
        const items = Array.isArray(_data) ? _data : [];
        const candidateItems = items
            .filter((it) => it?.lang === currentLangId)
            .filter((it) => {
                if (!inputValue) return true;
                return String(it?.label || '').toLowerCase().includes(String(inputValue || '').toLowerCase());
            })
            .slice(0, 40);

        const urls = Array.from(new Set(candidateItems.map(getAvatarUrl).filter(Boolean)));
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        let cancelled = false;
        const controllers = [];

        const shouldFetch = (url) => {
            if (!url) return false;
            if (avatarPreviewByUrl?.[url]) return false;
            if (objectUrlRef.current?.[url]) return false;
            if (url.startsWith('blob:') || url.startsWith('data:')) return false;
            if (!/^https?:\/\//i.test(url)) return false;
            return true;
        };

        const fetchOne = async (url) => {
            if (!shouldFetch(url)) return;
            const controller = new AbortController();
            controllers.push(controller);
            try {
                const res = await fetch(url, { headers, signal: controller.signal });
                if (!res.ok) return;
                const blob = await res.blob();
                const objUrl = URL.createObjectURL(blob);
                objectUrlRef.current[url] = objUrl;
                if (cancelled) return;
                setAvatarPreviewByUrl((prev) => ({ ...prev, [url]: objUrl }));
            } catch { }
        };

        urls.forEach((url) => { fetchOne(url); });

        return () => {
            cancelled = true;
            controllers.forEach((c) => {
                try { c.abort(); } catch { }
            });
        };
    }, [open, _data, currentLangId, inputValue]);

    const onSelectedHandler = (item) => {
        setSelectedItem({ ...item, label: item.label });
        setOpen(false);
        setInputValue("");
    }

    // ✅ دالة مساعدة مبسطة للحصول على مصدر الصورة
    const getImgSrc = (item) => {
        const url = getAvatarUrl(item);
        if (!url) return '';
        if (avatarFailedByUrl[url]) return '';
        return avatarPreviewByUrl[url] || url;
    };

    const renderAvatarFallback = (item, className = 'w-6 h-6 rounded-full object-cover flex-shrink-0 border border-slate-200') => {
        if (!showAvatarFallback || !item?.value) return null;
        const initial = String(item?.label || '?').trim().charAt(0).toUpperCase() || '?';
        return (
            <span
                className={`${className} inline-flex items-center justify-center bg-slate-200 text-slate-600 text-[10px] font-extrabold`}
                title={String(item?.label || '')}
            >
                {initial}
            </span>
        );
    };

    const dataList = _data && _data?.map((item, index) => {
        if (dataType === 'labelValue') {
            if (item?.lang === currentLangId) {
                const matchesSearch = !inputValue || item?.label?.toLowerCase().includes(inputValue.toLowerCase());
                const imgSrc = getImgSrc(item);
                return (
                    <div
                        className={`px-4 py-2.5 border-b border-slate-100 last:border-0 cursor-pointer text-sm hover:bg-indigo-50 hover:text-indigo-700 transition-colors ${matchesSearch ? 'block' : 'hidden'}`}
                        key={index}
                        onClick={() => onSelectedHandler(item)}
                    >
                        <div className="flex items-center gap-2" style={{ direction: currentLangId === 'ar' ? 'rtl' : 'ltr' }}>
                            {imgSrc ? (
                                <img
                                    src={imgSrc}
                                    alt={String(item?.label || '')}
                                    className="w-6 h-6 rounded-full object-cover flex-shrink-0 border border-slate-200"
                                    onError={() => {
                                        const url = getAvatarUrl(item);
                                        if (!url) return;
                                        setAvatarFailedByUrl((prev) => ({ ...prev, [url]: true }));
                                    }}
                                />
                            ) : (item?.dot || item?.color) ? (
                                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: String(item.dot || item.color) }} />
                            ) : renderAvatarFallback(item)}
                            <div className="flex-1 min-w-0">
                                <div className="truncate">{item.label}</div>
                                {(item?.subLabel || item?.jobTitle) ? (
                                    <div className="truncate text-[11px] text-slate-400">{item.subLabel || item.jobTitle}</div>
                                ) : null}
                            </div>
                        </div>
                    </div>
                )
            }
        }
        return null;
    }).filter(Boolean);

    // ✅ مصدر الصورة للعنصر المختار - مبسط وواضح
    const selectedAvatarUrl = normalizeAvatarUrl(currentAvatar);
    const selectedAvatarSrc = avatarFailedByUrl[selectedAvatarUrl] ? '' : (avatarPreviewByUrl[selectedAvatarUrl] || selectedAvatarUrl);

    return (
        <div ref={wrapperRef} className={`relative ${open ? 'z-[9999]' : 'z-10'} ${className || ''}`}>
            {withLabel && <label className='text-xs text-slate-500 font-semibold mb-1.5 block'> {label} </label>}
            
            <div
                onClick={() => { if (!disabled) setOpen(!open); }}
                className={`flex text-sm bg-white text-slate-700 w-full min-h-[38px] px-3 items-center justify-between rounded-lg border border-slate-200 transition-all duration-200 min-w-0 ${disabled ? 'cursor-not-allowed opacity-60 bg-slate-50' : 'cursor-pointer hover:border-indigo-400 hover:shadow-sm hover:bg-slate-50'} ${invalid ? 'border-red-500' : ''} ${styleList || ''}`}
            >
                <div className="flex items-center gap-2 flex-1 min-w-0" style={{ direction: currentLangId === 'ar' ? 'rtl' : 'ltr' }}>
                    {currentAvatar && selectedAvatarSrc ? (
                        <img
                            src={selectedAvatarSrc}
                            alt={String(selectedItem?.label || '')}
                            className="w-6 h-6 rounded-full object-cover flex-shrink-0 border border-slate-200"
                            onError={() => {
                                if (!selectedAvatarUrl) return;
                                setAvatarFailedByUrl((prev) => ({ ...prev, [selectedAvatarUrl]: true }));
                            }}
                        />
                    ) : currentDot ? (
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: String(currentDot) }} />
                    ) : renderAvatarFallback(selectedItem)}
                    <div className="flex-1 min-w-0">
                        <div className='truncate text-start font-medium'>
                            {(selectedItem && selectedItem !== 'sub' ? selectedItem.label : initalValue) || initalValue}
                        </div>
                        {currentSubLabel ? (
                            <div className="truncate text-[11px] text-slate-400 text-start">{currentSubLabel}</div>
                        ) : null}
                    </div>
                </div>
                <BiChevronDown size={18} className={`text-slate-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
            </div>
            
            <ul className={`bg-white border border-slate-200 border-t-0 start-0 absolute w-full rounded-b-lg shadow-lg z-[9999] ${open ? 'block' : 'hidden'}`} style={{ direction: currentLangId === 'ar' ? 'rtl' : 'ltr' }}>
                <div className='sticky top-0 p-3 bg-white z-10 border-b border-slate-100'>
                    {withAddNew && onClickNew && (
                        <div className='text-end mb-2'>
                            <div className='text-indigo-600 inline-flex items-center gap-1 cursor-pointer hover:text-indigo-800 transition-colors text-xs font-semibold'
                                onClick={() => {
                                    onClickNew();
                                    setOpen(false);
                                }}
                            >
                                <MdAddBox size={18} /> إضافة جديد
                            </div>
                        </div>
                    )}
                    <div className='flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg border border-slate-200'>
                        <AiOutlineSearch size={16} className='text-slate-400' />
                        <input
                            type="text"
                            placeholder="بحث..."
                            className='placeholder:text-slate-400 outline-none text-sm w-full bg-transparent'
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value.toLowerCase())}
                        />
                    </div>
                </div>
                <div className='max-h-60 overflow-y-auto'>
                    {dataList && dataList.length > 0 ? dataList : (
                        <div className="px-4 py-3 text-xs text-slate-400 text-center">لا توجد نتائج</div>
                    )}
                </div>
            </ul>
            {invalidMsg && <div className='text-xs text-red-500 mt-1'>{invalidMsg}</div>}
        </div>
    )
}

export default MwSelector
